/**
 * Force-lens example graphs for the gallery.
 *
 * Each example is a complete GraphData with status / confidence / bridging
 * baked in so every encoding dimension has something to show. These feed the
 * shared example registry (see registry.ts) alongside the other lenses.
 */

import type { GraphData, NodeSpec, EdgeSpec } from "./types.js";
import type { LensExample } from "./lens.js";

const now = "2026-03-05T00:00:00.000Z";

function graph(
  title: string,
  nodes: NodeSpec[],
  edges: EdgeSpec[],
): GraphData {
  return { nodes, edges, metadata: { title, lens: "force", created: now } };
}

// ── 1. Collective Intelligence topic network ───────────────────────────────

function collectiveIntelligence(): GraphData {
  const nodes: NodeSpec[] = [
    { id: "agent", label: "agent", value: 92, cluster: "agents", status: "hub" },
    { id: "orchestration", label: "orchestration", value: 61, cluster: "agents" },
    { id: "memory", label: "memory", value: 48, cluster: "agents" },
    { id: "tool-use", label: "tool use", value: 33, cluster: "agents" },
    { id: "subagent", label: "subagent", value: 21, cluster: "agents" },
    { id: "graph", label: "graph", value: 88, cluster: "kg", status: "trending" },
    { id: "node", label: "node", value: 54, cluster: "kg" },
    { id: "edge", label: "edge", value: 41, cluster: "kg" },
    { id: "centrality", label: "centrality", value: 29, cluster: "kg", confidence: 0.5 },
    { id: "cluster", label: "cluster", value: 37, cluster: "kg" },
    { id: "sensemaking", label: "sensemaking", value: 70, cluster: "sense", status: "active", clusters: ["sense", "kg"] },
    { id: "insight", label: "insight", value: 44, cluster: "sense" },
    { id: "gap", label: "gap", value: 26, cluster: "sense", status: "gap" },
    { id: "synthesis", label: "synthesis", value: 31, cluster: "sense" },
    { id: "visualization", label: "visualization", value: 64, cluster: "viz" },
    { id: "force", label: "force-directed", value: 38, cluster: "viz" },
    { id: "encoding", label: "encoding", value: 27, cluster: "viz" },
    { id: "glow", label: "glow", value: 14, cluster: "viz" },
  ];
  const edges: EdgeSpec[] = [
    { source: "agent", target: "orchestration", weight: 0.9 },
    { source: "agent", target: "memory", weight: 0.7 },
    { source: "agent", target: "tool-use", weight: 0.6 },
    { source: "orchestration", target: "subagent", weight: 0.8 },
    { source: "agent", target: "subagent", weight: 0.5 },
    { source: "graph", target: "node", weight: 0.95 },
    { source: "graph", target: "edge", weight: 0.9 },
    { source: "node", target: "edge", weight: 0.6 },
    { source: "graph", target: "centrality", weight: 0.5 },
    { source: "graph", target: "cluster", weight: 0.7 },
    { source: "centrality", target: "cluster", weight: 0.4 },
    { source: "sensemaking", target: "insight", weight: 0.8 },
    { source: "sensemaking", target: "synthesis", weight: 0.6 },
    { source: "sensemaking", target: "gap", weight: 0.5 },
    { source: "insight", target: "gap", weight: 0.4 },
    { source: "visualization", target: "force", weight: 0.85 },
    { source: "visualization", target: "encoding", weight: 0.7 },
    { source: "visualization", target: "glow", weight: 0.4 },
    { source: "force", target: "encoding", weight: 0.5 },
    { source: "graph", target: "visualization", weight: 0.6 },
    { source: "agent", target: "graph", weight: 0.55 },
    { source: "sensemaking", target: "graph", weight: 0.45 },
    { source: "memory", target: "sensemaking", weight: 0.35 },
    { source: "centrality", target: "gap", weight: 0.4 },
    { source: "encoding", target: "centrality", weight: 0.3 },
  ];
  return graph("Collective Intelligence — Topic Network", nodes, edges);
}

// ── 2. Multi-agent ecosystem ────────────────────────────────────────────────

function agentEcosystem(): GraphData {
  const bots = [
    ["logger", "Message Logger", 58],
    ["link", "Link Processor", 46],
    ["classifier", "Content Classifier", 41],
    ["bridge", "Context Bridge", 52],
    ["action", "Action Extractor", 38],
    ["insight", "Insight Synthesizer", 44],
    ["roam", "Roam Export", 22],
    ["kg", "Knowledge Graph", 67],
    ["factory", "Content Factory", 49],
  ] as const;
  const nodes: NodeSpec[] = [
    { id: "orchestrator", label: "Orchestrator", value: 96, cluster: "core", status: "hub", type: "agent" },
    { id: "memory", label: "Shared Memory", value: 74, cluster: "core", status: "trending", type: "concept", clusters: ["core", "knowledge"] },
    ...bots.map(([id, label, value]) => ({
      id,
      label,
      value,
      cluster: id === "kg" || id === "roam" ? "knowledge" : "bots",
      type: "agent" as const,
    })),
    { id: "vault", label: "Obsidian Vault", value: 81, cluster: "knowledge", status: "active", type: "document" },
    { id: "telegram", label: "Telegram", value: 35, cluster: "io", type: "concept" },
    { id: "gap-classify", label: "untriaged", value: 18, cluster: "bots", status: "gap", type: "action" },
  ];
  const edges: EdgeSpec[] = [
    { source: "orchestrator", target: "memory", weight: 0.95, type: "reference" },
    { source: "telegram", target: "logger", weight: 0.9, type: "causal", temporal: "recent" },
    { source: "logger", target: "memory", weight: 0.7 },
    { source: "link", target: "classifier", weight: 0.6 },
    { source: "classifier", target: "gap-classify", weight: 0.4, temporal: "recent" },
    { source: "classifier", target: "kg", weight: 0.7, type: "semantic" },
    { source: "bridge", target: "memory", weight: 0.8, type: "reference" },
    { source: "action", target: "memory", weight: 0.5 },
    { source: "insight", target: "kg", weight: 0.75, type: "semantic" },
    { source: "kg", target: "vault", weight: 0.9, type: "reference" },
    { source: "roam", target: "vault", weight: 0.5, temporal: "historical" },
    { source: "factory", target: "vault", weight: 0.6 },
    { source: "orchestrator", target: "logger", weight: 0.5 },
    { source: "orchestrator", target: "bridge", weight: 0.55 },
    { source: "orchestrator", target: "insight", weight: 0.5 },
    { source: "orchestrator", target: "factory", weight: 0.45 },
    { source: "memory", target: "vault", weight: 0.85, type: "reference" },
    { source: "insight", target: "factory", weight: 0.5, type: "causal" },
    { source: "bridge", target: "insight", weight: 0.4 },
  ];
  return graph("Multi-Agent Ecosystem — Telegram Bot Network", nodes, edges);
}

// ── 3. Knowledge vault (larger, with orphans + bridges) ──────────────────────

function knowledgeVault(): GraphData {
  const clusters = ["protocol", "research", "clients", "ops", "writing"];
  const nodes: NodeSpec[] = [];
  const edges: EdgeSpec[] = [];

  // A hub per cluster + satellites.
  const seeds: Record<string, [string, number][]> = {
    protocol: [["Totem Protocol", 90], ["MetaTotem", 52], ["Letta memory", 44], ["Identity root", 28], ["persona", 23]],
    research: [["InfraNodus", 71], ["knowledge graph", 63], ["betweenness", 33], ["modularity", 29], ["Engelbart", 25]],
    clients: [["ShurAI", 66], ["Condo Experts", 31], ["Sense Collective", 58], ["consulting", 27]],
    ops: [["Claude Code", 77], ["MCP server", 55], ["Obsidian", 69], ["DEVONthink", 30], ["Bases", 24]],
    writing: [["daily note", 48], ["content flywheel", 42], ["anti-slop", 26], ["handoff", 33]],
  };
  for (const c of clusters) {
    seeds[c].forEach(([label, value], i) => {
      const id = label.replace(/\s+/g, "-").toLowerCase();
      nodes.push({
        id,
        label,
        value,
        cluster: c,
        status: i === 0 ? "hub" : undefined,
        type: c === "writing" ? "document" : "concept",
      });
    });
    // intra-cluster spokes
    const ids = seeds[c].map(([l]) => l.replace(/\s+/g, "-").toLowerCase());
    for (let i = 1; i < ids.length; i++) {
      edges.push({ source: ids[0], target: ids[i], weight: 0.5 + 0.4 * Math.cos(i) });
    }
  }
  // cross-cluster bridges → the structurally interesting nodes
  const bridge = (a: string, b: string, w: number) => edges.push({ source: a, target: b, weight: w, type: "semantic" });
  bridge("infranodus", "knowledge-graph", 0.9);
  bridge("knowledge-graph", "totem-protocol", 0.7);
  bridge("mcp-server", "totem-protocol", 0.65);
  bridge("claude-code", "mcp-server", 0.8);
  bridge("obsidian", "daily-note", 0.85);
  bridge("obsidian", "bases", 0.6);
  bridge("content-flywheel", "shurai", 0.55);
  bridge("sense-collective", "engelbart", 0.5);
  bridge("letta-memory", "mcp-server", 0.45);
  bridge("handoff", "claude-code", 0.5);

  // a bridging node (spans research + protocol) and a couple of gaps/orphans
  const kg = nodes.find((n) => n.id === "knowledge-graph");
  if (kg) {
    kg.clusters = ["research", "protocol"];
    kg.status = "trending";
  }
  nodes.push({ id: "orphan-note", label: "unlinked draft", value: 9, cluster: "writing", status: "gap", type: "document" });
  nodes.push({ id: "betweenness-bridge", label: "GenAI Studio", value: 19, cluster: "clients", status: "gap", confidence: 0.6 });
  bridge("betweenness-bridge", "totem-protocol", 0.3);
  bridge("betweenness-bridge", "shurai", 0.3);

  return graph("Knowledge Vault — 25k-node Obsidian (excerpt)", nodes, edges);
}

// ── 4. Content pipeline (typed + temporal edges, flow widths) ────────────────

function contentPipeline(): GraphData {
  const nodes: NodeSpec[] = [
    { id: "sources", label: "Sources", value: 80, cluster: "intake", type: "concept", status: "hub" },
    { id: "transcripts", label: "Transcripts", value: 40, cluster: "intake", type: "document" },
    { id: "research", label: "Research", value: 36, cluster: "intake", type: "document" },
    { id: "synthesis", label: "Synthesis", value: 55, cluster: "process", type: "action", status: "active" },
    { id: "drafts", label: "Drafts", value: 47, cluster: "process", type: "document" },
    { id: "review", label: "Anti-slop Review", value: 33, cluster: "process", type: "action", clusters: ["process", "publish"] },
    { id: "social", label: "Social Posts", value: 50, cluster: "publish", type: "document", status: "trending" },
    { id: "longform", label: "Longform", value: 38, cluster: "publish", type: "document" },
    { id: "archive", label: "Archive", value: 22, cluster: "publish", type: "document" },
    { id: "stalled", label: "Stalled Queue", value: 16, cluster: "process", type: "action", status: "gap" },
  ];
  const edges: EdgeSpec[] = [
    { source: "transcripts", target: "sources", weight: 0.7, flow: 0.5 },
    { source: "research", target: "sources", weight: 0.6, flow: 0.4 },
    { source: "sources", target: "synthesis", weight: 0.95, flow: 0.9, type: "causal", temporal: "recent" },
    { source: "synthesis", target: "drafts", weight: 0.85, flow: 0.8, type: "causal" },
    { source: "drafts", target: "review", weight: 0.8, flow: 0.7, type: "causal" },
    { source: "drafts", target: "stalled", weight: 0.3, flow: 0.2, temporal: "historical" },
    { source: "review", target: "social", weight: 0.75, flow: 0.85, type: "causal", temporal: "recent" },
    { source: "review", target: "longform", weight: 0.6, flow: 0.55, type: "causal" },
    { source: "review", target: "archive", weight: 0.4, flow: 0.3, temporal: "historical" },
    { source: "social", target: "sources", weight: 0.35, flow: 0.25, type: "reference", temporal: "recent" },
  ];
  return graph("Content Flywheel — Pipeline Dynamics", nodes, edges);
}

export const FORCE_EXAMPLES: LensExample[] = [
  {
    id: "collective-intelligence",
    title: "Collective Intelligence",
    blurb:
      "A topic network across agents, knowledge graphs, sensemaking, and visualization — the default constellation. Shows all three glow tiers and a bridging node.",
    highlights: ["3-tier glow", "bridging stripe", "dynamic palette"],
    lens: "force",
    build: collectiveIntelligence,
  },
  {
    id: "agent-ecosystem",
    title: "Multi-Agent Ecosystem",
    blurb:
      "Nine specialized bots orbiting a shared-memory core, feeding a knowledge graph and vault. Typed and temporal edges, an untriaged gap node.",
    highlights: ["typed edges", "temporal dashes", "hub + active"],
    lens: "force",
    build: agentEcosystem,
  },
  {
    id: "knowledge-vault",
    title: "Knowledge Vault",
    blurb:
      "A larger excerpt of a 25k-node Obsidian vault. Cluster hubs, cross-cluster bridges, and orphan/gap notes that betweenness centrality surfaces.",
    highlights: ["scale", "gaps & orphans", "cross-cluster bridges"],
    lens: "force",
    build: knowledgeVault,
  },
  {
    id: "content-pipeline",
    title: "Content Flywheel",
    blurb:
      "Intake → synthesis → review → publish, with edge width carrying flow volume and dashes carrying recency. A stalled queue flagged as a gap.",
    highlights: ["edge width = flow", "causal edges", "stalled gap"],
    lens: "force",
    build: contentPipeline,
  },
];
